import { useState, useCallback, useMemo } from 'react';
import type { JSX } from 'react';
import { Alert, Card, Grid, Space, Spin } from 'antd';

import { useClinicalRecords, type ClinicalRecordFilters } from '../api/clinicalHistory.api';
import { useClinicalHistoryPermissions } from '../hooks/useClinicalHistoryPermissions';
import { ClinicalHistoryTable } from '../components/ClinicalHistoryTable';
import { ClinicalHistoryDrawer } from '../components/ClinicalHistoryDrawer';
import { hasClinicalHistoryNote } from '@erp/appointments/components/history/historyHelpers';
import { formatServiceLineLabel } from '../utils/serviceLineLabel';

import {
  useAppointments,
  type Appointment,
  useProfessionals,
} from '@erp/appointments/api/appointments.api';
import ResponsivePageHeading from '@components/layout/ResponsivePageHeading.tsx';

/**
 * Objetivo: Página principal para buscar, revisar y gestionar historias clínicas con soporte responsivo optimizado.
 */
export function ClinicalHistoryPage(): JSX.Element {
  const permissions = useClinicalHistoryPermissions();
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;

  const [filters, setFilters] = useState<ClinicalRecordFilters>({
    page: 1,
    page_size: 50,
    history_status: 'completed',
  });
  const [selectedRecordId, setSelectedRecordId] = useState<number | null>(null);

  // Queries
  const recordsQuery = useClinicalRecords(filters, {
    enabled: permissions.canViewClinicalHistory,
  });

  const appointmentsQuery = useAppointments(undefined, {
    enabled: permissions.canViewClinicalHistory,
  });

  const professionalsQuery = useProfessionals(
    {},
    {
      enabled: permissions.canViewClinicalHistory,
    },
  );

  const handleFiltersChange = useCallback(
    (newFilters: ClinicalRecordFilters) => {
      setFilters((prev) => ({
        ...prev,
        ...newFilters,
        page: newFilters.page ?? prev.page ?? 1,
        page_size: newFilters.page_size ?? prev.page_size ?? 50,
      }));
    },
    [],
  );

  const handleFiltersReset = useCallback(() => {
    setFilters({
      page: 1,
      page_size: 50,
      history_status: 'completed',
    });
  }, []);

  const handlePageChange = useCallback(
    (page: number, pageSize: number) => {
      setFilters((prev) => ({
        ...prev,
        page,
        page_size: pageSize,
      }));
    },
    [],
  );

  const handleViewRecord = useCallback((recordId: number) => {
    setSelectedRecordId(recordId);
  }, []);

  const handleCloseDrawer = useCallback(() => {
    setSelectedRecordId(null);
  }, []);

  const handleOpenAppointment = useCallback((appointmentId: number) => {
    setSelectedRecordId(appointmentId);
  }, []);

  const rawItems = useMemo(() => recordsQuery.data?.items || [], [recordsQuery.data?.items]);

  const filteredItems = useMemo(() => {
    let items = rawItems;
    const status = filters.history_status ?? 'completed';
    if (status === 'completed') {
      items = items.filter(hasClinicalHistoryNote);
    } else if (status === 'pending') {
      items = items.filter((r) => !hasClinicalHistoryNote(r));
    }

    if (filters.service_search && filters.service_search.trim().length > 0) {
      const q = filters.service_search.trim().toLowerCase();
      items = items.filter((r) =>
        r.service_lines.some((line) =>
          formatServiceLineLabel(line).toLowerCase().includes(q) ||
          (line.service_name && line.service_name.toLowerCase().includes(q))
        ),
      );
    }

    return items;
  }, [rawItems, filters.history_status, filters.service_search]);

  if (!permissions.canViewClinicalHistory) {
    return (
      <Card>
        <Alert
          type="warning"
          title="Acceso denegado"
          description="No tenés permisos para ver historias clínicas. Contactá al administrador."
          showIcon
        />
      </Card>
    );
  }

  const selectedRecord =
    rawItems.find((r) => r.id === selectedRecordId) || null;

  const appointments: Appointment[] = appointmentsQuery.data?.appointments || [];
  const staffDirectory = professionalsQuery.data?.items || [];

  const isLoadingResources = professionalsQuery.isLoading;

  return (
    <Space orientation="vertical" size={isMobile ? 10 : 16} style={{ width: '100%', padding: isMobile ? '12px' : '24px' }}>
      <ResponsivePageHeading
        title="Historias Clínicas"
        description={isMobile ? undefined : "Buscar, revisar y registrar información clínica de manera segura"}
      />

      {isLoadingResources ? (
        <Card>
          <Spin tip="Cargando recursos..." />
        </Card>
      ) : (
        <Card className={'history-main-card'} styles={{ body: { padding: isMobile ? '12px' : '24px' }}}>
          {recordsQuery.isError ? (
            <Alert
              type="error"
              title="Error al cargar historias clínicas"
              description={recordsQuery.error?.message || 'Ocurrió un error inesperado'}
              showIcon
            />
          ) : (
            <ClinicalHistoryTable
              records={filteredItems}
              loading={recordsQuery.isLoading}
              total={filteredItems.length}
              page={filters.page || 1}
              pageSize={filters.page_size || 50}
              filters={filters}
              staffDirectory={staffDirectory}
              onFiltersChange={handleFiltersChange}
              onResetFilters={handleFiltersReset}
              onPageChange={handlePageChange}
              onViewRecord={handleViewRecord}
            />
          )}
        </Card>
      )}

      <ClinicalHistoryDrawer
        open={selectedRecordId !== null}
        record={selectedRecord}
        appointments={appointments}
        staffDirectory={staffDirectory}
        onClose={handleCloseDrawer}
        onOpenAppointment={handleOpenAppointment}
      />
    </Space>
  );
}
